/* ============================================================
   MOONRISE — Product form custom element
   Pattern: Dawn-style <product-form> with Section Rendering API.

   Responsibilities:
     1. Watch variant inputs (swatches, native selects) → resolve to a
        matching variant from the embedded variants JSON.
     2. Re-render the section via /?section_id=<id>&variant=<id> so price,
        availability, badge, and featured media update without page reload.
     3. Sync ?variant= in the URL with history.replaceState.
     4. Tell <product-media-gallery> which thumbnail to surface.
     5. Submit the form via /cart/add.js through the shared cart-store
        (assets/cart-store.js) so cart-drawer opens & cart-count updates.
   ============================================================ */

(function () {
  'use strict';

  if (customElements.get('product-form')) return;

  /* /cart/add.js does not answer with a consistent shape. A plain rejection
     carries a string `description`, but when LINE ITEM PROPERTY validation
     fails — a gift card recipient email, say — `description` is an object
     keyed by field: { "email": ["is invalid"] }. Handing that straight to
     textContent printed "[object Object]". */
  function cartErrorText(err) {
    if (!err) return null;

    if (typeof err.description === 'string' && err.description) return err.description;

    var source = null;
    if (err.description && typeof err.description === 'object') source = err.description;
    else if (err.errors && typeof err.errors === 'object') source = err.errors;

    if (source) {
      var parts = [];
      Object.keys(source).forEach(function (key) {
        var value = source[key];
        var text = Array.isArray(value) ? value.join(', ') : String(value);
        var line = (key + ' ' + text).trim();
        parts.push(line.charAt(0).toUpperCase() + line.slice(1));
      });
      if (parts.length) return parts.join('. ') + '.';
    }

    if (typeof err.message === 'string' && err.message) return err.message;
    return null;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl || window.location.pathname;
      this.variantIdInput = this.querySelector('[data-variant-id-input]');
      this.variantsJsonEl = this.querySelector('[data-variants-json]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.addBtn = this.querySelector('[data-add-to-cart]');
      this.addBtnLabel = this.querySelector('[data-add-to-cart-label]');
      this.buyNowBtn = this.querySelector('[data-buy-it-now]');
      this.customSizeField = this.querySelector('[data-custom-size-field]');
      this.customSizeInput = this.querySelector('[data-custom-size-input]');
      this.customSizeError = this.querySelector('[data-custom-size-error]');
      this.customSizeTrigger = (((this.customSizeField && this.customSizeField.dataset.customSizeTrigger) || 'Custom Size')).toLowerCase();

      this.variants = [];
      if (this.variantsJsonEl) {
        try { this.variants = JSON.parse(this.variantsJsonEl.textContent); }
        catch (e) { this.variants = []; }
      }

      this.optionInputs = Array.from(this.querySelectorAll('[data-option-index]')).filter(function (el) {
        return el.matches('button.product-swatch') || el.matches('button.product-option-pill') || el.matches('select.product-select');
      });

      this._bindOptionInputs();
      this._bindSubmit();
      this._bindBuyNow();

      this._debouncedRefresh = debounce(this._refresh.bind(this), 90);

      this._bindCustomSize();
      this._toggleCustomSize();
    }

    /* ---------- Variant resolution ---------- */
    _readSelectedOptions() {
      var byIndex = {};
      this.optionInputs.forEach(function (el) {
        var idx = parseInt(el.dataset.optionIndex, 10);
        if (el.matches('select.product-select')) {
          byIndex[idx] = el.value;
        } else if (el.classList && el.classList.contains('is-active')) {
          byIndex[idx] = el.dataset.optionValue;
        }
      });
      // Selects always report a value; for swatch groups missing an
      // active state, fall back to the first option's value.
      var optionCount = Math.max.apply(null, this.optionInputs.map(function (el) {
        return parseInt(el.dataset.optionIndex, 10);
      })) + 1;
      if (!isFinite(optionCount)) optionCount = 0;
      var out = [];
      for (var i = 0; i < optionCount; i++) {
        if (byIndex[i] != null) {
          out.push(byIndex[i]);
        } else {
          var firstSwatch = this.querySelector('[data-option-index="' + i + '"][data-option-value]');
          out.push(firstSwatch ? firstSwatch.dataset.optionValue : null);
        }
      }
      return out;
    }

    _resolveVariant(selected) {
      if (!this.variants || !this.variants.length) return null;
      for (var i = 0; i < this.variants.length; i++) {
        var v = this.variants[i];
        var match = true;
        for (var j = 0; j < selected.length; j++) {
          if (selected[j] != null && v.options[j] !== selected[j]) {
            match = false; break;
          }
        }
        if (match) return v;
      }
      return null;
    }

    /* ---------- Option input wiring ---------- */
    _bindOptionInputs() {
      var self = this;
      this.optionInputs.forEach(function (el) {
        if (el.matches('select.product-select')) {
          el.addEventListener('change', function () {
            self._onOptionChange();
          });
        } else {
          el.addEventListener('click', function () {
            var idx = el.dataset.optionIndex;
            var siblings = self.querySelectorAll('[data-option-index="' + idx + '"][data-option-value]');
            siblings.forEach(function (sib) {
              sib.classList.toggle('is-active', sib === el);
              sib.setAttribute('aria-checked', sib === el ? 'true' : 'false');
            });
            self._onOptionChange();
          });
        }
      });
    }

    _onOptionChange() {
      var selected = this._readSelectedOptions();
      this._toggleCustomSize(selected);
      var variant = this._resolveVariant(selected);
      if (variant) {
        if (this.variantIdInput) this.variantIdInput.value = variant.id;
        this._updateLabels(selected);
        this._syncUrl(variant.id);
        this._notifyMediaSwap(variant);
      }
      this._debouncedRefresh(variant);
    }

    _updateLabels(selected) {
      var self = this;
      selected.forEach(function (val, i) {
        var label = self.querySelector('[data-option-value-label="' + i + '"]');
        if (label && val != null) label.textContent = val;
      });
    }

    _syncUrl(variantId) {
      if (!variantId || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    _notifyMediaSwap(variant) {
      if (!variant || !variant.featured_media) return;
      var gallery = document.querySelector('product-media-gallery');
      if (!gallery) return;
      gallery.dispatchEvent(new CustomEvent('product:variant-media', {
        detail: { mediaId: variant.featured_media.id }
      }));
    }

    /* ---------- Section Rendering API refresh ---------- */
    _refresh(variant) {
      if (!this.sectionId) return;
      var url = this.productUrl + '?section_id=' + encodeURIComponent(this.sectionId);
      if (variant && variant.id) url += '&variant=' + encodeURIComponent(variant.id);

      var self = this;
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');

          self._swapRegion(doc, '[data-product-price]');
          self._swapRegion(doc, '[data-product-sku]');
          self._swapRegion(doc, '[data-product-availability]');
          self._updateAddButtonFromDoc(doc, variant);
        })
        .catch(function () {
          // Silent: stale price will refresh on submit. Don't block UX.
        });
    }

    _swapRegion(doc, selector) {
      var fresh = doc.querySelector(selector);
      var current = this.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }

    _updateAddButtonFromDoc(doc, variant) {
      if (!this.addBtn) return;
      var available = variant ? variant.available : true;
      this.addBtn.disabled = !available;
      if (this.buyNowBtn) this.buyNowBtn.disabled = !available;
      if (this.addBtnLabel) {
        var soldOutText = this.dataset.soldOutLabel || 'Sold Out';
        var addText = this.dataset.addLabel || 'Add to Cart';
        var unavailableText = this.dataset.unavailableLabel || 'Unavailable';
        if (!variant) this.addBtnLabel.textContent = unavailableText;
        else this.addBtnLabel.textContent = available ? addText : soldOutText;
      }
    }

    /* ---------- AJAX submit ---------- */
    _bindSubmit() {
      var self = this;
      this.form.addEventListener('submit', function (e) {
        e.preventDefault();
        self._submit();
      });
    }

    _submit() {
      if (!window.Moonrise || !window.Moonrise.cart) {
        this.form.submit();
        return;
      }
      var variantId = parseInt(this.variantIdInput && this.variantIdInput.value, 10);
      var quantity = this._readQuantity();
      if (!variantId) return;
      if (!quantity) return;
      if (!this._validateCustomSize()) return;
      if (!this._validateRecipient()) return;

      this._setLoading(true);
      this._clearError();

      var self = this;
      window.Moonrise.cart.add(variantId, quantity, this._getProperties())
        .then(function () {
          self._setLoading(false);
          self._announceAdded();
        })
        .catch(function (err) {
          self._setLoading(false);
          self._showError(cartErrorText(err) || 'Unable to add to cart. Please try again.');
        });
    }

    /* ---------- Buy It Now (add → checkout) ---------- */
    _bindBuyNow() {
      if (!this.buyNowBtn) return;
      var self = this;
      this.buyNowBtn.addEventListener('click', function (e) {
        e.preventDefault();
        self._buyNow();
      });
    }

    _buyNow() {
      var checkoutUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root : '/') + 'checkout';

      // No AJAX cart available → fall back to a native submit straight to checkout.
      if (!window.Moonrise || !window.Moonrise.cart) {
        this.form.setAttribute('action', checkoutUrl);
        this.form.submit();
        return;
      }

      var variantId = parseInt(this.variantIdInput && this.variantIdInput.value, 10);
      var quantity = this._readQuantity();
      if (!variantId) return;
      if (!quantity) return;
      if (!this._validateCustomSize()) return;
      if (!this._validateRecipient()) return;

      this._setLoading(true, this.buyNowBtn);
      this._clearError();

      var self = this;
      window.Moonrise.cart.add(variantId, quantity, this._getProperties())
        .then(function () {
          // Go straight to checkout; do not open the drawer.
          window.location.href = checkoutUrl;
        })
        .catch(function (err) {
          self._setLoading(false, self.buyNowBtn);
          self._showError(cartErrorText(err) || 'Unable to start checkout. Please try again.');
        });
    }

    /* The quantity box can hold 0 or nothing when Enter submits before blur
       re-clamps it. Previously that silently added 1; now it's refused with a
       message so what's added always matches what the box shows. */
    _readQuantity() {
      var qtyInput = this.form.querySelector('[name="quantity"]');
      if (!qtyInput) return 1;
      var quantity = parseInt(qtyInput.value, 10);
      if (isNaN(quantity) || quantity < 1) {
        this._showError('Please enter a quantity of 1 or more.');
        qtyInput.focus();
        return null;
      }
      return quantity;
    }

    _setLoading(on, btn) {
      var target = btn || this.addBtn;
      if (!target) return;
      target.classList.toggle('is-loading', !!on);
      target.setAttribute('aria-busy', on ? 'true' : 'false');
    }

    _announceAdded() {
      var drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        // cart-store has already published 'cart:updated' → drawer's
        // innards refresh; we just need to flip it open.
        setTimeout(function () { drawer.open(); }, 60);
      }
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    _clearError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    /* ---------- Custom ring size (Fine Jewelry) ---------- */
    _bindCustomSize() {
      if (!this.customSizeInput) return;
      var self = this;
      this.customSizeInput.addEventListener('input', function () {
        self._clearCustomSizeError();
      });
    }

    _isCustomSizeSelected(selected) {
      var values = selected || this._readSelectedOptions();
      for (var i = 0; i < values.length; i++) {
        if (values[i] && values[i].toLowerCase() === this.customSizeTrigger) return true;
      }
      return false;
    }

    _toggleCustomSize(selected) {
      if (!this.customSizeField || !this.customSizeInput) return;
      if (this._isCustomSizeSelected(selected)) {
        this.customSizeField.hidden = false;
        this.customSizeInput.disabled = false;
      } else {
        this.customSizeField.hidden = true;
        this.customSizeInput.disabled = true;
        this.customSizeInput.value = '';
        this._clearCustomSizeError();
      }
    }

    _validateCustomSize() {
      if (!this.customSizeField || this.customSizeField.hidden || !this.customSizeInput) return true;
      var val = (this.customSizeInput.value || '').trim();
      if (!val) {
        this._showCustomSizeError('Please enter your custom ring size.');
        try { this.customSizeInput.focus(); } catch (e) { /* noop */ }
        return false;
      }
      return true;
    }

    /* Line item properties, read live from the form at submit time.

       This used to hand-pick the Custom ring size field, which meant any other
       properties[...] input in the form was silently dropped: the AJAX path
       preventDefaults the submit and builds its own payload, so the browser
       never serialises the form itself. Reading them generically lets a
       section add properties without touching this file.

       Custom ring size is unchanged by this: it is a properties[...] input
       like any other, so it comes through the same path with the same name
       and value. Disabled inputs are skipped by FormData exactly as a native
       submit would skip them, which is what lets a hidden, disabled field
       contribute nothing. */
    /* The product form carries novalidate, so the browser never enforces
       `required` or type="email" on submit. Without this, an empty or
       malformed recipient email went to Shopify and came back as a 422.

       Scoped to the gift card element: reporting validity across the whole
       form would start enforcing constraints on every other product too. */
    _validateRecipient() {
      var recipient = this.querySelector('gift-card-recipient');
      if (!recipient) return true;

      var fields = recipient.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
      );
      for (var i = 0; i < fields.length; i++) {
        if (!fields[i].checkValidity()) {
          /* novalidate suppresses the automatic bubble, so ask for it. */
          if (typeof fields[i].reportValidity === 'function') fields[i].reportValidity();
          try { fields[i].focus(); } catch (e) { /* noop */ }
          return false;
        }
      }
      return true;
    }

    _getProperties() {
      if (!this.form) return null;

      var props = null;
      var data;
      try {
        data = new FormData(this.form);
      } catch (e) {
        return null;
      }

      data.forEach(function (value, name) {
        if (name.slice(0, 11) !== 'properties[' || name.slice(-1) !== ']') return;
        var key = name.slice(11, -1);
        if (!key) return;
        /* Shopify's own underscore-prefixed flags carry meaning even when the
           value is empty, so only trim-and-drop the customer-facing ones. */
        var val = typeof value === 'string' ? value : '';
        if (key.charAt(0) !== '_') {
          val = val.trim();
          if (!val) return;
        }
        if (!props) props = {};
        props[key] = val;
      });

      return props;
    }

    _showCustomSizeError(message) {
      if (this.customSizeInput) this.customSizeInput.classList.add('is-invalid');
      if (!this.customSizeError) return;
      this.customSizeError.textContent = message;
      this.customSizeError.hidden = false;
    }

    _clearCustomSizeError() {
      if (this.customSizeInput) this.customSizeInput.classList.remove('is-invalid');
      if (!this.customSizeError) return;
      this.customSizeError.textContent = '';
      this.customSizeError.hidden = true;
    }
  }

  customElements.define('product-form', ProductForm);
})();
